import { HttpStatusCode, type AxiosResponse } from "axios";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Guid from "_shared/guids/guid";
import Individual from "marketplace/individuals/individual";
import BulkMessageAPIRequest from "messaging/_api/commands/bulk-message-api-command";
import CreateMessageAPIRequest from "messaging/_api/commands/create-message-api-command";
import MessageQuery from "messaging/_api/queries/message-query";
import {
  BulkMessageResponseZ,
  bulkMessageIssues,
  isBulkMessageSuccess,
} from "messaging/_schemas/bulk-message-response-schema";
import MessageSchema from "messaging/_schemas/message-schema";
import MessageHistorySchema from "messaging/_schemas/message-history-schema";
import AttachmentQuery from "messaging/attachments/_api/queries/attachment-query";
import MessageFileInfoSchema from "messaging/attachments/_schemas/message-file-info-schema";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import ForumQuery from "messaging/forums/_api/queries/forum-query";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Forum from "messaging/forums/forum";
import MarketplaceSubscriberInfoAPIResponse from "messaging/forums/subscribers/_api/responses/marketplace-subscriber-info-api-response";
import ForumSubscriberInvitationCommand from "messaging/forums/subscribers/invitations/_apis/commands/forum-subscriber-invitation-api-command";
import Message from "messaging/message";
import MessageHistory from "messaging/message-history";
import z from "zod";
import { validateGuid, validateMessage, validateTopic } from "messaging/_api/messaging-validation";

const JSON_HEADERS = { "Content-Type": "application/json" };

/** Wire shape of one entry from `POST messaging/forums/messages/by-ids`: a forum id and its messages. */
const FORUM_MESSAGES_RESPONSE_Z = z.object({
  forumId: z.string(),
  messages: z.array(MessageSchema.MessageJSONZ),
});

export type ForumStoreTarget =
  | { kind: "byTopic"; topicKey: string }
  | { kind: "byQuery"; queryKey: string }
  | { kind: "commentThreadsByProposal"; proposalId: string };

type MessageListArgs = {
  forumId: string;
  query: MessageQuery;
  append: boolean;
};

type MessageListByForumsArgs = {
  forumIds: Array<Guid | string>;
  query: MessageQuery;
};

type MessageIdArgs = {
  forumId: string;
  messageId: string;
};

type AttachmentIdArgs = {
  forumId: string;
  fileId: string;
};

type ForumListArgs = {
  query: ForumQuery;
  storeTarget?: ForumStoreTarget;
};

type ForumIdsArgs = {
  ids: Array<Guid | string>;
};

type ForumIdArgs = {
  forumId: string;
};

type ForumMutationArgs = {
  forum: Forum;
  /** Stable across a user retry so the server can resume an unknown-result operation. */
  operationId: Guid | string;
};

export type ForumOperation = {
  operationId: string;
  operationType: string;
  status: string;
  forumId: string | null;
  messagingStage: string;
  marketplaceStage: string;
  workStage: string;
  permissionsStage: string;
};

const FORUM_OPERATION_Z = z.object({
  operationId: z.uuid(),
  operationType: z.string(),
  status: z.string(),
  forumId: z.uuid().nullable(),
  messagingStage: z.string(),
  marketplaceStage: z.string(),
  workStage: z.string(),
  permissionsStage: z.string(),
});

type ForumSubscriberInfoArgs = {
  forumId: string;
};

type InviteSubscriberToForumArgs = {
  forumId: string;
  inviteeId: Guid | string;
  invitationMessage?: string;
};

type AcceptForumInvitationArgs = {
  invitationId: string;
};

type AttachmentListArgs = {
  forumId: string;
  query: AttachmentQuery;
  append: boolean;
};

export const messagingApi = api.injectEndpoints({
  endpoints: (build) => ({
    getForums: build.query<ForumJSON[], ForumListArgs>({
      query: ({ query }) => ({
        url: "messaging/forums",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(ForumSchema.ForumJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): ForumJSON[] =>
        ForumSchema.validateJSONArray(z.array(ForumSchema.ForumJSONZ).parse(response)),
      providesTags: [{ type: "Forum", id: "LIST" }],
    }),
    getForumsByIds: build.query<ForumJSON[], ForumIdsArgs>({
      query: ({ ids }) => {
        if (ids.length === 0) throw new Error("At least one forum is required.");
        for (const id of ids) {
          const error = validateGuid(id, "Forum");
          if (error) throw new Error(error);
        }
        return ({
        url: "messaging/forums/by-ids",
        method: "POST",
        body: JSON.stringify(ids.map(toGuidValue)),
        headers: JSON_HEADERS,
        });
      },
      rawResponseSchema: z.array(ForumSchema.ForumJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): ForumJSON[] =>
        ForumSchema.validateJSONArray(z.array(ForumSchema.ForumJSONZ).parse(response)),
      providesTags: [{ type: "Forum", id: "LIST" }],
    }),
    createForum: build.mutation<ForumJSON, ForumMutationArgs>({
      query: ({ forum, operationId }) => {
        const topicError = validateTopic(forum.topic);
        if (topicError) throw new Error(topicError);
        return {
        url: "messaging/forums",
        method: "POST",
        body: JSON.stringify({ ...forum.toJSON(), operationId: toGuidValue(operationId) }),
        headers: JSON_HEADERS,
        };
      },
      rawResponseSchema: ForumSchema.ForumJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): ForumJSON => ForumSchema.validateJSON(response),
      invalidatesTags: [{ type: "Forum", id: "LIST" }],
    }),
    deleteForum: build.mutation<void, ForumIdArgs>({
      query: ({ forumId }) => ({
        url: `messaging/forums/${forumId}`,
        method: "DELETE",
      }),
      onQueryStarted: async ({ forumId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(messagingApi.util.upsertQueryData("getForumsByIds", { ids: [forumId] }, []));
        } catch {
          // Failed deletes should leave existing forum-by-id query data intact.
        }
      },
    }),
    resolveForum: build.mutation<void, ForumIdArgs>({
      query: ({ forumId }) => ({
        url: `messaging/forums/${forumId}/resolve`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: [{ type: "Forum", id: "LIST" }],
    }),
    reopenForum: build.mutation<void, ForumIdArgs>({
      query: ({ forumId }) => ({
        url: `messaging/forums/${forumId}/reopen`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: [{ type: "Forum", id: "LIST" }],
    }),
    updateForumSubscribers: build.mutation<void, ForumMutationArgs>({
      query: ({ forum, operationId }) => {
        if (!forum.id) {
          throw new Error("Forum must have an id to update subscribers");
        }
        return {
          url: `messaging/forums/${forum.id.value}/subscribers`,
          method: "PUT",
          body: JSON.stringify({
            operationId: toGuidValue(operationId),
            subscribers:
              forum.subscribers?.map((subscriber) => ({
                userId: subscriber.userId,
                legalEntityId: subscriber.legalEntityId,
              })) ?? [],
          }),
          headers: JSON_HEADERS,
        };
      },
      invalidatesTags: (_result, _error, { forum: _forum }) => [
        { type: "Forum", id: "LIST" },
        { type: "ForumSubscriber", id: "LIST" },
      ],
    }),
    getForumOperation: build.query<ForumOperation, { operationId: Guid | string }>({
      query: ({ operationId }) => `messaging/operations/${toGuidValue(operationId)}`,
      rawResponseSchema: FORUM_OPERATION_Z,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): ForumOperation => FORUM_OPERATION_Z.parse(response),
    }),
    getForumSubscriberInfo: build.query<Individual[], ForumSubscriberInfoArgs>({
      query: ({ forumId }) => `messaging/forums/${forumId}/subscribers`,
      transformResponse: (response: unknown): Individual[] => {
        if (!Array.isArray(response)) return [];
        return response
          .map((info: Partial<MarketplaceSubscriberInfoAPIResponse>) =>
            Object.assign(new MarketplaceSubscriberInfoAPIResponse({}), info),
          )
          .map((info) => info.deserialize());
      },
      providesTags: [{ type: "ForumSubscriber", id: "LIST" }],
    }),
    inviteSubscriberToForum: build.mutation<void, InviteSubscriberToForumArgs>({
      query: ({ forumId, inviteeId, invitationMessage }) => {
        const forumError = validateGuid(forumId, "Forum");
        const inviteeError = validateGuid(inviteeId, "Invitee");
        if (forumError || inviteeError) throw new Error(forumError ?? inviteeError);
        if (invitationMessage !== undefined && invitationMessage.trim().length === 0) {
          throw new Error("Invitation message cannot be blank.");
        }
        return {
        url: `messaging/forums/${forumId}/invite-subscriber`,
        method: "POST",
        body: JSON.stringify(new ForumSubscriberInvitationCommand(toGuid(inviteeId), invitationMessage)),
        headers: JSON_HEADERS,
        };
      },
      invalidatesTags: (_result, _error, { forumId: _forumId }) => [
        { type: "ForumSubscriber", id: "LIST" },
        { type: "ForumInvitation", id: "LIST" },
      ],
    }),
    acceptForumInvitation: build.mutation<void, AcceptForumInvitationArgs>({
      query: ({ invitationId }) => {
        const error = validateGuid(invitationId, "Invitation");
        if (error) throw new Error(error);
        return ({
        url: `messaging/forum-invitations/${invitationId}/accept`,
        method: "POST",
        body: {},
        });
      },
      invalidatesTags: (_result, _error, { invitationId }) => [
        { type: "ForumInvitation", id: invitationId },
        { type: "ForumInvitation", id: "LIST" },
      ],
    }),
    getAttachmentsByForum: build.query<PaginatedResponse<MessageFileInfo>, AttachmentListArgs>({
      query: ({ forumId, query }) => ({
        url: `messaging/forums/${forumId}/attachments`,
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(MessageFileInfoSchema.MessageFileInfoJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown, meta): PaginatedResponse<MessageFileInfo> => {
        const fileInfoJSON = z.array(MessageFileInfoSchema.MessageFileInfoJSONZ).parse(response);
        const attachments = MessageFileInfo.fromJSONArray(fileInfoJSON);
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return PaginatedResponse.from<MessageFileInfo>(attachments, headers);
      },
      providesTags: [{ type: "MessageAttachment", id: "LIST" }],
    }),
    getMessagesByForum: build.query<PaginatedResponse<Message>, MessageListArgs>({
      query: ({ forumId, query }) => ({
        url: `messaging/forums/${forumId}/messages`,
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(MessageSchema.MessageJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown, meta): PaginatedResponse<Message> => {
        const messageJSON = z.array(MessageSchema.MessageJSONZ).parse(response);
        const messages = Message.fromJSONArray({ jsonArray: messageJSON });
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return PaginatedResponse.from<Message>(messages, headers);
      },
      providesTags: [{ type: "Message", id: "LIST" }],
    }),
    /**
     * The batch sibling of `getMessagesByForum`: one request for every thread's messages instead of one
     * per forum, so a proposal's comment-thread pre-load stops paying an N+1. Keyed by forum id (a
     * `CommentThread`'s own id) rather than returned as a flat list, since a forum with no messages
     * still needs an empty entry to distinguish "loaded, nothing there" from "not requested."
     */
    getMessagesByForums: build.query<Record<string, Message[]>, MessageListByForumsArgs>({
      query: ({ forumIds, query }) => {
        if (forumIds.length === 0) throw new Error("At least one forum is required.");
        for (const forumId of forumIds) {
          const error = validateGuid(forumId, "Forum");
          if (error) throw new Error(error);
        }
        return ({
        url: "messaging/forums/messages/by-ids",
        method: "POST",
        params: query.searchParams,
        body: JSON.stringify(forumIds.map(toGuidValue)),
        headers: JSON_HEADERS,
        });
      },
      rawResponseSchema: z.array(FORUM_MESSAGES_RESPONSE_Z),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): Record<string, Message[]> => {
        const entries = z.array(FORUM_MESSAGES_RESPONSE_Z).parse(response);
        const messagesByForumId: Record<string, Message[]> = {};
        for (const entry of entries) {
          messagesByForumId[entry.forumId] = Message.fromJSONArray({ jsonArray: entry.messages });
        }
        return messagesByForumId;
      },
      providesTags: [{ type: "Message", id: "LIST" }],
    }),
    createMessage: build.mutation<Message, { message: Message }>({
      query: ({ message }) => {
        const errors = validateMessage(message);
        if (Object.keys(errors).length > 0) throw new Error(Object.values(errors)[0]);
        const forumId = message.forum.id;
        if (!forumId) throw new Error("Message must have a forum id to be created");
        const request = new CreateMessageAPIRequest(message);
        return {
          url: `messaging/forums/${forumId.value}/messages`,
          method: "POST",
          body: request.formData,
        };
      },
      onQueryStarted: async ({ message }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          // A conflict response means the authorized forum changed. Refresh once so the user can decide what
          // to do with the stale draft; never replay the multipart write.
          const rejected = typeof error === "object" && error !== null && "error" in error
            ? (error as { error?: unknown }).error
            : error;
          const status = typeof rejected === "object" && rejected !== null && "status" in rejected
            ? (rejected as { status?: unknown }).status
            : undefined;
          if (status === HttpStatusCode.Conflict && message.forum.id) {
            const request = dispatch(messagingApi.endpoints.getForumsByIds.initiate(
              { ids: [message.forum.id] },
              { forceRefetch: true, subscribe: false },
            ));
            await request.unwrap().catch(() => undefined);
            request.unsubscribe();
          }
        }
      },
      rawResponseSchema: MessageSchema.MessageJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): Message => {
        const params = MessageSchema.deserialize(response);
        return new Message(params);
      },
    }),
    createBulkMessages: build.mutation<Message[], { messages: Message[] }>({
      async queryFn({ messages }, _api, _extraOptions, fetchWithBQ) {
        if (!messages.length) {
          console.warn("No messages to create");
          return { data: [] };
        }

        let request: BulkMessageAPIRequest;
        try {
          request = new BulkMessageAPIRequest(messages);
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: error instanceof Error ? error.message : "Invalid message batch." } };
        }
        const result = await fetchWithBQ({
          url: "messaging/bulk-messages",
          method: "POST",
          body: request.payload,
          headers: JSON_HEADERS,
        });

        if (result.error) return { error: result.error };

        // The batch answers 200 even when individual messages failed, so the outcome is per entry.
        // Both the parse failure and the reported-issue case return an error rather than throwing:
        // this runs inside a queryFn, where a throw escapes as an unhandled rejection instead of a
        // failed mutation, which is how a save could fail forever without ever saying why.
        const parsed = z.array(BulkMessageResponseZ).safeParse(result.data);
        if (!parsed.success) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "The server returned an unrecognized response while saving messages.",
            },
          };
        }

        const issues = bulkMessageIssues(parsed.data);
        if (issues.length > 0) {
          return {
            error: { status: "CUSTOM_ERROR", error: issues.join(" "), data: issues },
          };
        }

        const messageJSON = parsed.data.filter((entry) => isBulkMessageSuccess(entry));
        return { data: Message.fromJSONArray({ jsonArray: messageJSON }) };
      },
      invalidatesTags: (_result, _error, { messages: _messages }) => [
        { type: "Message", id: "LIST" },
      ],
    }),
    deleteMessage: build.mutation<void, MessageIdArgs>({
      query: ({ forumId, messageId }) => ({
        url: `messaging/forums/${forumId}/messages/${messageId}`,
        method: "DELETE",
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(messagingApi.util.upsertQueryData("getMessageImages", args, null));
        } catch {
          // Failed deletes should leave existing message image query data intact.
        }
      },
    }),
    getMessageImages: build.query<MessageFileInfo[] | null, MessageIdArgs>({
      query: ({ forumId, messageId }) => `messaging/forums/${forumId}/messages/${messageId}/images`,
      rawResponseSchema: z.array(MessageFileInfoSchema.MessageFileInfoJSONZ).nullable(),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): MessageFileInfo[] | null => {
        if (response === null) return null;
        const imageJSON = z.array(MessageFileInfoSchema.MessageFileInfoJSONZ).parse(response);
        return MessageFileInfo.fromJSONArray(imageJSON);
      },
      providesTags: (_result, _error, { messageId }) => [{ type: "Message", id: messageId }],
    }),
    getMessageHistory: build.query<MessageHistory[], MessageIdArgs>({
      query: ({ forumId, messageId }) => `messaging/forums/${forumId}/messages/${messageId}/history`,
      rawResponseSchema: z.array(MessageHistorySchema.MessageHistoryJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: unknown): MessageHistory[] => {
        const historyJSON = z.array(MessageHistorySchema.MessageHistoryJSONZ).parse(response);
        return MessageHistory.fromJSONArray(historyJSON);
      },
      providesTags: (_result, _error, { messageId }) => [{ type: "Message", id: messageId }],
    }),
    downloadMessageAttachment: build.query<AxiosResponse<Blob>, AttachmentIdArgs>({
      query: ({ forumId, fileId }) => ({
        url: `messaging/forums/${forumId}/attachments/${fileId}`,
        responseHandler: async (response: Response) => {
          const headers: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            headers[key] = value;
          });

          return {
            data: await response.blob(),
            headers,
            request: {
              getResponseHeader: (name: string) => response.headers.get(name),
            },
          } as AxiosResponse<Blob>;
        },
      }),
      providesTags: (_result, _error, { forumId, fileId }) => [
        { type: "MessageAttachment", id: fileId },
        { type: "MessageAttachment", id: `FORUM-${forumId}` },
      ],
    }),
  }),
});

export const {
  useGetForumsQuery,
  useLazyGetForumsQuery,
  useGetForumsByIdsQuery,
  useLazyGetForumsByIdsQuery,
  useCreateForumMutation,
  useDeleteForumMutation,
  useResolveForumMutation,
  useReopenForumMutation,
  useUpdateForumSubscribersMutation,
  useLazyGetForumOperationQuery,
  useGetForumSubscriberInfoQuery,
  useLazyGetForumSubscriberInfoQuery,
  useInviteSubscriberToForumMutation,
  useAcceptForumInvitationMutation,
  useGetAttachmentsByForumQuery,
  useLazyGetAttachmentsByForumQuery,
  useGetMessagesByForumQuery,
  useLazyGetMessagesByForumQuery,
  useGetMessagesByForumsQuery,
  useLazyGetMessagesByForumsQuery,
  useCreateMessageMutation,
  useCreateBulkMessagesMutation,
  useDeleteMessageMutation,
  useGetMessageImagesQuery,
  useLazyGetMessageHistoryQuery,
  useLazyDownloadMessageAttachmentQuery,
} = messagingApi;

function toGuid(value: Guid | string): Guid {
  return value instanceof Guid ? value : new Guid(value);
}

function toGuidValue(value: Guid | string): string {
  return value instanceof Guid ? value.value : value;
}
